import api from './api';

export type LabelStats = {
  total: number;
  false_positive: number;
  missed_detection: number;
  with_frame: number;
  pool_ids?: number[];
  pool_count?: number;
};

export type YoloExportResult = {
  pool_id: number;
  pool_ids?: number[];
  dataset_dir: string;
  yaml_path: string;
  total_labels: number;
  exported_images: number;
  skipped_no_frame: number;
  train_images: number;
  val_images: number;
  missed_detection: number;
  false_positive: number;
};

export type TrainJobStatus = {
  running?: boolean;
  pool_id?: number;
  pool_ids?: number[];
  started_at?: string;
  finished_at?: string | null;
  success?: boolean | null;
  message?: string;
  export?: YoloExportResult;
  model_path?: string;
};

export async function fetchDetectionLabelStats(
  poolIds: number | number[],
): Promise<LabelStats> {
  const ids = (Array.isArray(poolIds) ? poolIds : [poolIds])
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (ids.length === 0) {
    throw new Error('시설을 선택');
  }
  if (ids.length === 1) {
    return api.get<LabelStats>(
      '/api/detection-labels/stats',
      { pool_id: ids[0] },
      { silentError: true, useCache: false },
    );
  }
  return api.get<LabelStats>(
    '/api/detection-labels/stats',
    { pool_ids: ids.join(',') },
    { silentError: true, useCache: false },
  );
}

export async function exportDetectionLabelsYolo(
  poolIds: number | number[],
  valRatio = 0.2,
): Promise<YoloExportResult> {
  const ids = (Array.isArray(poolIds) ? poolIds : [poolIds])
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (ids.length === 1) {
    return api.post<YoloExportResult>('/api/detection-labels/export-yolo', {
      pool_id: ids[0],
      val_ratio: valRatio,
    });
  }
  return api.post<YoloExportResult>('/api/detection-labels/export-yolo', {
    pool_ids: ids,
    val_ratio: valRatio,
  });
}

export type StartTrainParams = {
  pool_id?: number;
  pool_ids?: number[];
  epochs?: number;
  imgsz?: number;
  batch?: number;
  device?: string;
  val_ratio?: number;
  /** 파인튜닝 시작 weights (models/ 파일명 또는 절대경로) */
  base_model?: string;
  copy_to_ver5?: boolean;
  /** 운영 기본 weights 파일명 (models/ 아래). copy_to_ver5 시 이 이름으로 복사 */
  copy_to_filename?: string;
};

export async function startDetectionLabelTraining(
  params: StartTrainParams,
): Promise<TrainJobStatus> {
  return api.post<TrainJobStatus>('/api/detection-labels/train', params);
}

export async function fetchDetectionLabelTrainingStatus(): Promise<TrainJobStatus> {
  return api.get<TrainJobStatus>('/api/detection-labels/train/status', undefined, {
    silentError: true,
  });
}

export type DetectionLabelRow = {
  id: string;
  created_at: string;
  pool_id: number;
  cctv_id: number;
  label_type: string;
  bbox?: number[] | null;
  track_id?: number | null;
  class_name?: string | null;
  note?: string | null;
  frame_path?: string | null;
  frame_width?: number | null;
  frame_height?: number | null;
};

export async function fetchDetectionLabelList(params: {
  poolIds: number[];
  labelType?: '' | 'false_positive' | 'missed_detection';
  limit?: number;
}): Promise<DetectionLabelRow[]> {
  const ids = params.poolIds
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!ids.length) return [];
  const query: Record<string, string | number> = {
    limit: params.limit ?? 100,
  };
  if (ids.length === 1) query.pool_id = ids[0];
  else query.pool_ids = ids.join(',');
  if (params.labelType) query.label_type = params.labelType;
  return api.get<DetectionLabelRow[]>('/api/detection-labels/', query, {
    silentError: true,
    useCache: false,
  });
}

export function detectionLabelFrameUrl(poolId: number, labelId: string): string {
  return `/api/detection-labels/${poolId}/${encodeURIComponent(labelId)}/frame`;
}

export async function deleteDetectionLabel(
  poolId: number,
  labelId: string,
): Promise<void> {
  await api.delete(`/api/detection-labels/${poolId}/${encodeURIComponent(labelId)}`);
}
